import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';

@Component({
    selector: 'app-language-selector',
    template: `
<mat-form-field  appearance="outline" class="w-100" color="primary">
  <mat-label>{{ 'ITEM.INVOICE_LANGUAGE' | translate}}</mat-label>
  <input
    type="text"
    matInput
    [formControl]="languageControl"
    [matAutocomplete]="auto"
    placeholder="Select language"
  />
    <mat-error *ngIf="languageControl.hasError('required')">
    {{ 'MESSAGES.REQUIRED_SELECT' | translate }}
    </mat-error>
  <mat-autocomplete #auto="matAutocomplete">
    <mat-option *ngFor="let lang of filteredLanguages | async" [value]="lang.label">
      {{ lang.label }}
    </mat-option>
  </mat-autocomplete>
</mat-form-field>
  `,
})
export class LanguageSelectorComponent implements OnInit {
    languages = InvoiceLanguages;

    @Output() languageChanged = new EventEmitter<string>();

    @Input() defaultLang: string;

    languageControl = new FormControl('', Validators.required);
    filteredLanguages!: Observable<{ code: string; label: string }[]>;

    ngOnInit(): void {
        const browserLang = navigator.language.toLowerCase();
        const direct = this.languages.find(l => l.code.toLowerCase() === browserLang);
        const fallback = this.languages.find(l => l.code === browserLang.split('-')[0]);


        const defaultLabel = this.getLabelFromCode(this.defaultLang)
            || direct?.label
            || fallback?.label
            || 'English';

        this.languageControl.setValue(defaultLabel);

        this.filteredLanguages = this.languageControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );

        this.languageControl.valueChanges.subscribe(val => {
            this._validateLanguage();
            const selectedCode = this.getSelectedLanguageCode();
            this.languageChanged.emit(selectedCode);
        });

        const selectedCode = this.getSelectedLanguageCode();
        this.languageChanged.emit(selectedCode);
    }


    private _validateLanguage() {
        const selectedLanguage = this.languageControl.value;
        const isValid = this.languages.some(lang => lang.label === selectedLanguage);
        if (!isValid) {
            this.languageControl.setErrors({ required: true });
        } else {
            this.languageControl.setErrors(null);
        }
    }

    private _filter(value: string): { code: string; label: string }[] {
        const filterValue = value.toLowerCase();
        return this.languages.filter(lang =>
            lang.label.toLowerCase().includes(filterValue) || lang.code.toLowerCase().includes(filterValue)
        );
    }

    getSelectedLanguageCode(): string {
        const selected = this.languages.find(
            lang => lang.label === this.languageControl.value || lang.code === this.languageControl.value
        );
        return selected?.code || 'en';
    }

    private getLabelFromCode(code?: string): string | undefined {
        return this.languages.find(l => l.code === code)?.label;
    }
}


export const InvoiceLanguages = [
    { code: 'auto', label: 'Auto (based on browser language)' },
    { code: 'bg', label: 'Bulgarian (български)' },
    { code: 'cs', label: 'Czech (čeština)' },
    { code: 'da', label: 'Danish (dansk)' },
    { code: 'de', label: 'German (Deutsch)' },
    { code: 'el', label: 'Greek (ελληνικά)' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish (español)' },
    { code: 'et', label: 'Estonian (eesti)' },
    { code: 'fi', label: 'Finnish (suomi)' },
    { code: 'fr', label: 'French (français)' },
    { code: 'he', label: 'Hebrew (עברית)' },
    { code: 'hr', label: 'Croatian (hrvatski)' },
    { code: 'hu', label: 'Hungarian (magyar)' },
    { code: 'id', label: 'Indonesian (Indonesia)' },
    { code: 'it', label: 'Italian (italiano)' },
    { code: 'ja', label: 'Japanese (日本語)' },
    { code: 'ko', label: 'Korean (한국어)' },
    { code: 'lt', label: 'Lithuanian (lietuvių)' },
    { code: 'lv', label: 'Latvian (latviešu)' },
    { code: 'ms', label: 'Malay (Melayu)' },
    { code: 'mt', label: 'Maltese (Malti)' },
    { code: 'nb', label: 'Norwegian Bokmål (norsk bokmål)' },
    { code: 'nl', label: 'Dutch (Nederlands)' },
    { code: 'pl', label: 'Polish (polski)' },
    { code: 'pt', label: 'Portuguese (português)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil) (português do Brasil)' },
    { code: 'ro', label: 'Romanian (română)' },
    { code: 'ru', label: 'Russian (русский)' },
    { code: 'sk', label: 'Slovak (slovenčina)' },
    { code: 'sl', label: 'Slovenian (slovenščina)' },
    { code: 'sv', label: 'Swedish (svenska)' },
    { code: 'th', label: 'Thai (ไทย)' },
    { code: 'tr', label: 'Turkish (Türkçe)' },
    { code: 'vi', label: 'Vietnamese (Tiếng Việt)' },
    { code: 'zh', label: 'Chinese (中文)' },
    { code: 'zh-HK', label: 'Chinese (Hong Kong) (中文（香港）)' },
    { code: 'zh-TW', label: 'Chinese (Taiwan) (中文（台灣）)' }
];